import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';

export function StaffManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'rider' });
  const [error, setError] = useState('');
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const load = () => api.get('/admin/users').then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    setError('');
    if (!form.name || !form.password) return setError('Name and password are required.');
    if (form.role !== 'rider' && !form.email) return setError('Email is required for admin accounts.');
    try {
      await api.post('/admin/users', form);
      toast.success(`${form.role === 'admin' ? 'Admin' : 'Rider'} account created`, { description: form.email ? `${form.email} can now log in directly with the password you set.` : 'Rider login ID was auto-generated.' });
      setForm({ name: '', email: '', password: '', role: 'rider' });
      load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete account ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('Account deleted');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const ROLE_STYLE = { admin: 'bg-kelate-darkwood text-kelate-surface', rider: 'bg-kelate-sage text-white', merchant: 'bg-kelate-wood text-white', consumer: 'bg-secondary text-kelate-ink' };
  return (
    <div data-testid="admin-staff" className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Staff Accounts</h2>
        <p className="text-xs text-kelate-soft">Create rider or admin accounts — they log in directly with the email and password you set. Roles: SUPER_ADMIN, OPERATIONS_ADMIN, FINANCE_ADMIN, MARKETING_ADMIN, SUPPORT_ADMIN, DELIVERY_ADMIN (mapped to admin/rider in this prototype).</p>
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Create Account</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input data-testid="staff-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          <input data-testid="staff-email" type="email" placeholder={form.role === 'rider' ? 'Email (optional — auto-generated if blank)' : 'Email (login ID)'} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
          <input data-testid="staff-password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
          <select data-testid="staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input}>
            <option value="rider">Rider (Delivery Staff)</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {error && <p className="mt-2 text-xs text-kelate-red" data-testid="staff-error">{error}</p>}
        <button data-testid="staff-create" onClick={submit} className="mt-4 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
          Create Account
        </button>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">All Accounts</h3>
        <div className="space-y-2" data-testid="staff-list">
          {users === null && <p className="text-sm text-kelate-soft">Loading...</p>}
          {users?.map((u) => (
            <div key={u.id} data-testid={`staff-row-${u.email}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 px-4 py-2.5 text-sm">
              <div>
                <span className="font-medium text-kelate-ink">{u.name}</span>
                <span className="ml-2 text-xs text-kelate-soft">{u.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ROLE_STYLE[u.role] || ROLE_STYLE.consumer}`}>{u.role}</span>
                {u.id !== currentUser?.id && (
                  <button data-testid={`staff-delete-${u.id}`} onClick={() => remove(u)} className="rounded-lg p-1.5 text-kelate-soft hover:bg-kelate-red/10 hover:text-kelate-red" title="Delete account">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {users?.length === 0 && <p className="text-sm text-kelate-soft">No accounts found.</p>}
        </div>
      </section>
    </div>
  );
}


export function SupportAdmin() {
  const { supportTickets, replyTicket, setTicketStatus } = useApp();
  const { user } = useAuth();
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState('');
  const statusStyle = { OPEN: 'bg-kelate-red/10 text-kelate-red', IN_PROGRESS: 'bg-kelate-wood/10 text-kelate-wood', RESOLVED: 'bg-kelate-sage/15 text-kelate-sage' };
  const openTicket = supportTickets.find((t) => t.id === openId);
  return (
    <div data-testid="admin-support" className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Support Tickets</h2>
        <p className="text-xs text-kelate-soft">Customer tickets from My KELATE. Reply and resolve here — the customer sees your reply instantly.</p>
      </div>
      {supportTickets.length === 0 && <EmptyState icon={LifeBuoy} title="No tickets" description="Customer support tickets will appear here." />}
      <div className="space-y-2">
        {supportTickets.map((t) => (
          <div key={t.id} data-testid={`admin-ticket-${t.id}`} className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
            <button data-testid={`admin-ticket-open-${t.id}`} onClick={() => { setOpenId(openId === t.id ? null : t.id); setReply(''); }} className="flex w-full flex-wrap items-center justify-between gap-2 text-left">
              <div>
                <span className="text-sm font-semibold text-kelate-ink">{t.subject}</span>
                <span className="ml-2 text-xs text-kelate-soft">{t.name} · {t.category} · {timeAgo(t.createdAt)}</span>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyle[t.status] || statusStyle.OPEN}`}>{t.status.replace('_', ' ')}</span>
            </button>
            {openId === t.id && (
              <div className="mt-3 border-t border-kelate-line/60 pt-3">
                <div className="space-y-2">
                  <div className="rounded-2xl bg-secondary/60 px-3 py-2 text-xs text-kelate-ink">{t.message}</div>
                  {t.replies.map((r, i) => (
                    <div key={i} className={`rounded-2xl px-3 py-2 text-xs ${r.byAdmin ? 'ml-6 bg-kelate-darkwood text-kelate-surface' : 'mr-6 bg-secondary/60 text-kelate-ink'}`}>
                      <span className="mb-0.5 block text-[10px] uppercase tracking-wide opacity-70">{r.from} · {timeAgo(r.at)}</span>
                      {r.message}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <input data-testid={`ticket-reply-input-${t.id}`} placeholder="Reply to customer..." value={reply} onChange={(e) => setReply(e.target.value)} className="h-10 flex-1 rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
                  <button data-testid={`ticket-reply-send-${t.id}`} onClick={() => { if (reply.trim()) { replyTicket(t.id, user?.name || 'KELATE Support', reply.trim(), true); setReply(''); } }} className="rounded-xl bg-kelate-darkwood px-4 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Reply</button>
                </div>
                <div className="mt-2 flex gap-2">
                  {t.status !== 'RESOLVED' ? (
                    <button data-testid={`ticket-resolve-${t.id}`} onClick={() => setTicketStatus(t.id, 'RESOLVED')} className="rounded-xl bg-kelate-sage px-4 py-2 text-xs font-semibold text-white">Mark Resolved</button>
                  ) : (
                    <button data-testid={`ticket-reopen-${t.id}`} onClick={() => setTicketStatus(t.id, 'OPEN')} className="rounded-xl border border-kelate-line px-4 py-2 text-xs font-medium text-kelate-soft">Reopen</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReferralsAdmin() {
  const { referralProgram, referralUses, updateReferralProgram } = useApp();
  const [cfg, setCfg] = useState(referralProgram);
  useEffect(() => setCfg(referralProgram), [referralProgram]);
  const totalDiscount = referralUses.reduce((s, u) => s + u.discount, 0);
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  return (
    <div data-testid="admin-referrals" className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Referral Program</h2>
        <p className="text-xs text-kelate-soft">Customers share their referral code; friends get a discount at checkout. Discounts are funded by KELATE, never deducted from merchant settlements.</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Total Referrals" value={referralUses.length} testId="ref-total-uses" />
        <KpiCard label="Discount Given" value={fmtRM2(totalDiscount)} testId="ref-total-discount" />
        <KpiCard label="Status" value={referralProgram.enabled ? 'Active' : 'Off'} testId="ref-status" />
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-kelate-ink">Programme settings</h3>
            <p className="text-[11px] text-kelate-soft">Buyer discount applied at checkout when a valid referral code is used.</p>
          </div>
          <Switch data-testid="referral-enabled-toggle" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-kelate-soft">Discount type</label>
            <select data-testid="referral-type" value={cfg.discountType} onChange={(e) => setCfg({ ...cfg, discountType: e.target.value })} className={input}>
              <option value="FIXED">Fixed amount (RM)</option>
              <option value="PERCENT">Percentage (%)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-kelate-soft">{cfg.discountType === 'PERCENT' ? 'Percent off' : 'Amount off (RM)'}</label>
            <input data-testid="referral-value" type="number" min="0" step="0.5" value={cfg.discountValue} onChange={(e) => setCfg({ ...cfg, discountValue: Number(e.target.value) })} className={input} />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-kelate-soft">Minimum spend (RM)</label>
            <input data-testid="referral-min-spend" type="number" min="0" value={cfg.minSpend} onChange={(e) => setCfg({ ...cfg, minSpend: Number(e.target.value) })} className={input} />
          </div>
        </div>
        <button data-testid="referral-save" onClick={() => updateReferralProgram(cfg)} className="mt-4 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Save Program</button>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Referral Usage</h3>
        {referralUses.length === 0 ? (
          <p className="text-xs text-kelate-soft" data-testid="referral-uses-empty">No referral codes used yet.</p>
        ) : (
          <div className="space-y-2">
            {referralUses.map((u) => (
              <div key={u.id} data-testid={`referral-use-${u.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-2.5 text-xs">
                <span className="font-mono font-bold text-kelate-darkwood">{u.code}</span>
                <span className="text-kelate-soft">{u.ownerName} → <b className="text-kelate-ink">{u.usedByName}</b></span>
                <span className="text-kelate-soft">Order {u.orderNumber}</span>
                <span className="font-semibold text-kelate-sage">-{fmtRM2(u.discount)}</span>
                <span className="text-kelate-soft">{timeAgo(u.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

